import React, { useEffect, useState, useRef, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.fullscreen";
import "leaflet.fullscreen/Control.FullScreen.css";
import { locationImgNew } from "../assets";
import {
  useGetEventConfigurationQuery,
  useGetGeofenceQuery,
  useLazyGetGeofenceQuery,
} from "../../ManagementPortal/services";
import { getLocalizedString } from "../../../shared/translation";
import { getAllEventPositions, createGeofenceLayer, fitMapBoundsToGeofences } from "./utils";

const EMPTY_EVENTS = [];
const NOOP = () => {};

const EventsMap = ({
  deviceId,
  mapPoint,
  mapExpand,
  dockAlign,
  selectedRowId,
  showEventsDetails,
  setDockAlign,
  maximizeMinimize,
  mapProvider,
  mapboxAccessToken,
  googleMapsApiKey,
  eventsData = EMPTY_EVENTS, // Array of events to show markers for
  onEventMarkerClick = NOOP, // Callback for when event marker is clicked
}) => {
  const [map, setMap] = useState(null);
  const [geofenceData, setGeofenceData] = useState({});
  const countRef = useRef(0);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const polygonRef = useRef(null);
  const mapContainerRef = useRef(null);
  const currentZoomRef = useRef(14);
  const fitBoundsTimeoutRef = useRef(null); // Add timeout ref for cleanup
  const allEventMarkersRef = useRef([]); // Store references to all event markers
  const allGeofenceLayersRef = useRef([]); // Store references to all geofence layers
  const lastAutoFitKeyRef = useRef(null);

  // Get Geofence Data (24.3)
  const { data: geoResponse } = useGetGeofenceQuery(
    showEventsDetails?.events?.config?.geofence_id && {
      id: showEventsDetails?.events?.config?.geofence_id,
    }
  );

  const [getGeofence] = useLazyGetGeofenceQuery();

  useEffect(() => {
    if (geoResponse?.json_data) {
      setGeofenceData(JSON.parse(geoResponse?.json_data));
    }
  }, [geoResponse]);

  // Extract unique geofence IDs from events data
  const geofenceIds = React.useMemo(() => {
    if (!eventsData?.length) return [];

    const geofenceEvents = eventsData.filter(
      (event) =>
        (event?.events?.type === "geofence" && event?.events?.config?.geofence_id) ||
        (event?.events?.type === "speeding" &&
          event?.events?.sub_type === "geofence" &&
          event?.events?.config?.geofence_id)
    );

    return [...new Set(geofenceEvents.map((event) => event.events.config.geofence_id))].filter(
      Boolean
    );
  }, [eventsData]);

  // Lazy geofence data fetching - only fetch when geofence IDs exist
  const [geofencesData, setGeofencesData] = useState({});
  const [geofencesLoading, setGeofencesLoading] = useState(false);

  // Fetch geofence data when geofence IDs change
  useEffect(() => {
    if (!geofenceIds.length) {
      setGeofencesData({});
      setGeofencesLoading(false);
      return;
    }

    const fetchGeofences = async () => {
      setGeofencesLoading(true);
      try {
        for (const geofenceId of geofenceIds) {
          const { data } = await getGeofence({ id: geofenceId });

          if (data?.json_data) {
            try {
              const geofenceData = JSON.parse(data.json_data);
              setGeofencesData((prev) => ({ ...prev, [geofenceId]: geofenceData }));
            } catch (parseError) {
              // Set fallback data
              setGeofencesData((prev) => ({
                ...prev,
                [geofenceId]: {
                  type: "Circle",
                  radius: 500,
                  coordinates: [],
                },
              }));
            }
          } else {
            console.warn(`No json_data for geofence ${geofenceId}`);
            // Set fallback data
            setGeofencesData((prev) => ({
              ...prev,
              [geofenceId]: {
                type: "Circle",
                radius: 500,
                coordinates: [],
              },
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching geofences:", error);
      } finally {
        setGeofencesLoading(false);
      }
    };

    fetchGeofences();
  }, [geofenceIds, getGeofence]);

  // Clear all event markers utility function
  const clearAllEventMarkers = () => {
    if (allEventMarkersRef.current && map) {
      allEventMarkersRef.current.forEach((marker) => {
        try {
          map.removeLayer(marker);
        } catch (e) {
          console.warn("Error removing event marker:", e);
        }
      });
      allEventMarkersRef.current = [];
    }
  };

  // Clear all geofence layers utility function
  const clearAllGeofenceLayers = () => {
    if (allGeofenceLayersRef.current && map) {
      allGeofenceLayersRef.current.forEach((layer) => {
        try {
          map.removeLayer(layer);
        } catch (e) {
          console.warn("Error removing geofence layer:", e);
        }
      });
      allGeofenceLayersRef.current = [];
    }
  };

  // Add all event markers to map
  const addAllEventMarkers = (shouldFit = false) => {
    if (!map || !eventsData?.length) return;

    // Clear existing markers first
    clearAllEventMarkers();

    const allPositions = getAllEventPositions(eventsData);

    if (allPositions.length === 0) return;

    // Create markers for each event
    allPositions.forEach((positionData, index) => {
      const { lat, lng, eventIndex, eventData } = positionData;

      try {
        // Get the label text (plate number or device name)
        const labelText =
          eventData?.plate_number ||
          eventData?.device_name ||
          eventData?.source_id ||
          `Event ${eventIndex + 1}`;

        // Create custom HTML for marker with label
        const markerHtml = `
          <div style="position: relative; text-align: center;">
            <div style="
              position: absolute;
              top: -25px;
              left: 50%;
              transform: translateX(-50%);
              background: #fff;
              color: #000;
              padding: 4px 8px;
              border: 1px solid #999;
              border-radius: 4px;
              font: 12px Calibri, sans-serif;
              white-space: nowrap;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              z-index: 1000;
            ">${labelText}</div>
            <img src="${locationImgNew}" style="width: 28px; height: 32px;" />
          </div>
        `;

        const eventMarker = L.marker([lat, lng], {
          icon: L.divIcon({
            html: markerHtml,
            iconSize: [28, 32],
            iconAnchor: [14, 30],
            className: "custom-event-marker", // Custom CSS class for styling
          }),
          name: `event_marker_${index}`,
        });

        // Add click handler for event marker
        eventMarker.on("click", () => {
          onEventMarkerClick(eventData, eventIndex);
        });

        // Add popup with event info
        eventMarker.bindPopup(() => {
          const eventType = eventData?.events?.type || "Event";
          const eventName =
            eventData?.events?.config?.name ||
            eventData?.events?.config?.Config?.name ||
            "Unknown Event";
          return `<div><strong>${eventType}</strong><br/>${eventName}<br/>Plate: ${labelText}<br/>${getLocalizedString(
            "latitude",
            "Latitude"
          )}: ${Number(lat).toFixed(5)}, ${getLocalizedString("longitude", "Longitude")}: ${Number(
            lng
          ).toFixed(5)}</div>`;
        });

        eventMarker.addTo(map);
        allEventMarkersRef.current.push(eventMarker);
      } catch (e) {
        console.warn("Error adding event marker:", e);
      }
    });

    // Add geofences for geofence events after a short delay to ensure map is ready
    setTimeout(() => {
      if (!selectedRowId) addAllGeofences();
    }, 500);

    // After adding geofences, fit bounds to include all geofences and markers
    setTimeout(() => {
      if (shouldFit && map && allEventMarkersRef.current.length > 0) {
        const cleanup = fitMapBoundsToGeofences(
          map,
          [],
          allEventMarkersRef.current,
          mapPoint, // fallback to world view
          5, // fallback zoom
          300 // delay to ensure geofences are rendered
        );

        // Store cleanup function for later use
        if (fitBoundsTimeoutRef.current) {
          fitBoundsTimeoutRef.current(); // Clear previous timeout
        }
        fitBoundsTimeoutRef.current = cleanup;
      }
    }, 800); // Wait for geofences to be added
  };

  // Add all geofences for geofence events
  const addAllGeofences = () => {
    if (!map || !eventsData?.length || geofencesLoading) {
      return;
    }

    // Clear existing geofence layers first
    clearAllGeofenceLayers();

    // Get all geofence events
    const geofenceEvents = eventsData.filter(
      (event) =>
        (event?.events?.type === "geofence" && event?.events?.config?.geofence_id) ||
        (event?.events?.type === "speeding" &&
          event?.events?.sub_type === "geofence" &&
          event?.events?.config?.geofence_id)
    );

    // Group events by geofence ID to avoid duplicates
    const geofenceGroups = {};
    geofenceEvents.forEach((event) => {
      const geofenceId = event.events.config.geofence_id;
      if (!geofenceGroups[geofenceId]) {
        geofenceGroups[geofenceId] = [];
      }
      geofenceGroups[geofenceId].push(event);
    });

    // For each unique geofence, create a layer
    Object.entries(geofenceGroups).forEach(([geofenceId, events]) => {
      if (!geofenceId) return;

      try {
        // Get the actual geofence data from the API response
        const geofenceData = geofencesData[geofenceId];

        // Try to create geofence layer using real data
        let geofenceLayer = createGeofenceLayer(geofenceData, `overview_geofence_${geofenceId}`);

        if (geofenceLayer) {
          geofenceLayer.addTo(map);
          allGeofenceLayersRef.current.push(geofenceLayer);
        }
      } catch (e) {
        console.warn("Error adding geofence layer:", e);
      }
    });
  };

  useEffect(() => {
    // Initialize the map with dynamic tile layer based on map provider
    const initialCenter = [20, 0]; // Set a better initial center point
    const mapInstance = L.map("device-map-container", {
      center: initialCenter,
      zoom: 14,
      minZoom: 2,
      // Adjust maxZoom based on provider
      maxZoom: mapProvider === "google" ? 20 : mapProvider === "mapbox" ? 22 : 19,
      // Enable fullscreen control
      fullscreenControl: true,
      fullscreenControlOptions: {
        position: dockAlign === "split-left" ? "topright" : "topleft", // Position the fullscreen button at top-right when docked left, else top-left
      },
    });

    // Select tile layer based on map provider
    let tileLayer;
    switch (mapProvider) {
      case "mapbox":
        if (mapboxAccessToken) {
          tileLayer = L.tileLayer(
            `https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=${mapboxAccessToken}`,
            {
              attribution: "© Mapbox",
              tileSize: 512,
              zoomOffset: -1,
              maxZoom: 22,
              errorTileUrl:
                "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7", // Transparent tile for errors
            }
          );
        }
        break;
      case "google":
        if (googleMapsApiKey) {
          tileLayer = L.tileLayer(
            `https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&key=${googleMapsApiKey}`,
            {
              attribution: "© Google Maps",
              maxZoom: 20,
              errorTileUrl:
                "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
            }
          );
        }
        break;
      default:
        // Default to OpenStreetMap for qiMap
        tileLayer = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap contributors",
          maxZoom: 19,
          errorTileUrl:
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
        });
    }

    // Add tile layer if created
    if (tileLayer) {
      tileLayer.addTo(mapInstance);
    }

    // Add zoom end event listener to store user's zoom preference
    mapInstance.on("zoomend", () => {
      currentZoomRef.current = mapInstance.getZoom();
    });

    // Add fullscreen event listeners to handle map resize
    mapInstance.on("enterFullscreen", () => {
      setTimeout(() => {
        if (mapInstance._mapPane) {
          mapInstance.invalidateSize();
        }
      }, 100);
    });

    mapInstance.on("exitFullscreen", () => {
      setTimeout(() => {
        if (mapInstance._mapPane) {
          mapInstance.invalidateSize();
        }
      }, 100);
    });

    // Force the zoom level to 14 after initialization
    mapInstance.setView(initialCenter, 14, { animate: false });

    setMap(mapInstance);

    // Clean up on component unmount
    return () => {
      if (allEventMarkersRef.current) {
        allEventMarkersRef.current.forEach((marker) => {
          try {
            mapInstance.removeLayer(marker);
          } catch (e) {
            console.warn("Error removing event marker on cleanup:", e);
          }
        });
        allEventMarkersRef.current = [];
      }
      if (allGeofenceLayersRef.current) {
        allGeofenceLayersRef.current.forEach((layer) => {
          try {
            mapInstance.removeLayer(layer);
          } catch (e) {
            console.warn("Error removing geofence layer on cleanup:", e);
          }
        });
        allGeofenceLayersRef.current = [];
      }
      mapInstance.remove();
    };
  }, [mapProvider, mapboxAccessToken, googleMapsApiKey]);

  useEffect(() => {
    try {
      if (!map || !map.getContainer() || !document.getElementById("device-map-container")) {
        return;
      }

      // Clear any pending fitBounds timeout
      if (fitBoundsTimeoutRef.current) {
        clearTimeout(fitBoundsTimeoutRef.current);
        fitBoundsTimeoutRef.current = null;
      }

      // Validate mapPoint coordinates if present
      const isValidCoords =
        mapPoint &&
        typeof mapPoint.lat === "number" &&
        typeof mapPoint.lng === "number" &&
        !isNaN(mapPoint.lat) &&
        !isNaN(mapPoint.lng) &&
        mapPoint.lat >= -90 &&
        mapPoint.lat <= 90 &&
        mapPoint.lng >= -180 &&
        mapPoint.lng <= 180;

      const eventPositionsKey = getAllEventPositions(eventsData)
        .map((position) => `${position.lat},${position.lng}`)
        .join("|");
      const viewportDataKey =
        isValidCoords && deviceId && (selectedRowId || selectedRowId === 0)
          ? `selected:${selectedRowId}:${mapPoint.lat},${mapPoint.lng}:${
              Object.keys(geofenceData).length
            }`
          : eventsData?.length
            ? `overview:${eventPositionsKey}:${Object.keys(geofencesData).sort().join(",")}`
            : "empty";
      const shouldAutoFit = lastAutoFitKeyRef.current !== viewportDataKey;

      if (isValidCoords && deviceId && (selectedRowId || selectedRowId === 0)) {
        // Reset counter when switching to new selection
        countRef.current = 0;

        // Clear all event markers and geofences when showing specific event
        clearAllEventMarkers();
        clearAllGeofenceLayers();

        // Remove previous layers
        if (markerRef?.current && map) {
          try {
            map.removeLayer(markerRef?.current);
          } catch (e) {
            console.warn("Error removing marker layer:", e);
          }
        }
        if (circleRef?.current && map) {
          try {
            map.removeLayer(circleRef?.current);
          } catch (e) {
            console.warn("Error removing circle layer:", e);
          }
        }
        if (polygonRef?.current && map) {
          try {
            map.removeLayer(polygonRef?.current);
          } catch (e) {
            console.warn("Error removing polygon layer:", e);
          }
        }

        // Draw Track Marker (without label when zoomed in)
        const trackMarker = L?.marker(mapPoint, {
          icon: L?.icon({
            iconUrl: locationImgNew,
            iconSize: [34, 40],
            iconAnchor: [17, 38], // Center horizontally, bottom vertically
          }),
          name: `track_${countRef?.current}`,
        });

        if (trackMarker && map) {
          try {
            trackMarker.addTo(map);
            markerRef.current = trackMarker;

            trackMarker?.bindPopup(() => {
              return `<div>${getLocalizedString("latitude", "Latitude")}: ${Number(
                mapPoint?.lat
              ).toFixed(5)}, ${getLocalizedString("longitude", "Longitude")}: ${Number(
                mapPoint?.lng
              ).toFixed(5)} </div>`;
            });
          } catch (e) {
            console.warn("Error adding marker to map:", e);
          }
        }

        // Draw selected event geofence
        let geofenceLayer = null;
        if (
          (showEventsDetails?.events?.type === "geofence" ||
            showEventsDetails?.events?.sub_type === "geofence") &&
          map &&
          geofenceData &&
          Object.keys(geofenceData).length > 0
        ) {
          // Create geofence layer using reusable function
          geofenceLayer = createGeofenceLayer(
            geofenceData,
            `selected_geofence_${countRef.current}`
          );

          if (geofenceLayer) {
            geofenceLayer.addTo(map);
            // Store reference based on type
            if (geofenceData.type === "Circle") {
              circleRef.current = geofenceLayer;
            } else if (geofenceData.type === "Polygon") {
              polygonRef.current = geofenceLayer;
            }
          }
        }

        // Fit bounds to include both marker and geofence when geofence is present
        if (shouldAutoFit && geofenceLayer && trackMarker && map) {
          // Use reusable function for fitting bounds to geofence and marker
          const cleanup = fitMapBoundsToGeofences(
            map,
            [trackMarker],
            [geofenceLayer],
            mapPoint,
            14, // fallback zoom
            200 // delay
          );

          // Store cleanup function for later use
          fitBoundsTimeoutRef.current = cleanup;
        } else if (shouldAutoFit) {
          // Use zoom level 14 for marker only view
          // Use zoom level 14 only for first time viewing a point when no geofence
          const zoomLevel = countRef.current === 0 ? 14 : currentZoomRef.current;

          try {
            if (map.getContainer()) {
              map.setView(mapPoint, zoomLevel);
            }
          } catch (e) {
            console.warn("Error setting map view:", e);
          }
        }

        if (shouldAutoFit) lastAutoFitKeyRef.current = viewportDataKey;

        countRef.current += 1;
      } else {
        // Clear specific event markers and geofence when no event is selected
        if (markerRef.current && map) {
          try {
            map.removeLayer(markerRef.current);
            markerRef.current = null;
          } catch (e) {
            console.warn("Error removing marker layer:", e);
          }
        }
        if (circleRef.current && map) {
          try {
            map.removeLayer(circleRef.current);
            circleRef.current = null;
          } catch (e) {
            console.warn("Error removing circle layer:", e);
          }
        }
        if (polygonRef.current && map) {
          try {
            map.removeLayer(polygonRef.current);
            polygonRef.current = null;
          } catch (e) {
            console.warn("Error removing polygon layer:", e);
          }
        }

        // Show all event markers when no specific event is selected
        if (map && map.getContainer() && eventsData?.length > 0) {
          addAllEventMarkers(shouldAutoFit);
          if (shouldAutoFit) lastAutoFitKeyRef.current = viewportDataKey;
        } else if (shouldAutoFit && map && map.getContainer()) {
          // No events data - show world view
          clearAllEventMarkers();
          clearAllGeofenceLayers();
          try {
            map.setView([20, 0], 2);
            lastAutoFitKeyRef.current = viewportDataKey;
          } catch (e) {
            console.warn("Error resetting map view:", e);
          }
        }

        countRef.current = 0; // Reset counter
      }
    } catch (e) {
      console.error("Error updating map:", e.message);
      // Reset to world view on error
      if (map && map.getContainer()) {
        try {
          map.setView([20, 0], 2);
          if (markerRef.current) map.removeLayer(markerRef.current);
          if (circleRef.current) map.removeLayer(circleRef.current);
          if (polygonRef.current) map.removeLayer(polygonRef.current);
          countRef.current = 0;
        } catch (e) {
          console.warn("Error handling map error state:", e);
        }
      }
    }

    // Cleanup function to clear timeout on unmount or dependency change
    return () => {
      if (fitBoundsTimeoutRef.current) {
        clearTimeout(fitBoundsTimeoutRef.current);
        fitBoundsTimeoutRef.current = null;
      }
    };
  }, [
    map,
    deviceId,
    mapPoint,
    selectedRowId,
    dockAlign,
    mapExpand,
    showEventsDetails,
    geofenceData,
    setDockAlign,
    eventsData,
    onEventMarkerClick,
    geofencesData,
    geofencesLoading,
  ]);

  // Update map size and take up the whole space when dockAlign or mapExpand changes
  useEffect(() => {
    if (map && map._mapPane && mapContainerRef?.current && document.getElementById("device-map-container")) {
      try {
        requestAnimationFrame(() => {
          if (map._mapPane) {
            map.invalidateSize();
          }
        });
      } catch (e) {
        console.warn("Error invalidating map size:", e);
      }
    }
  }, [map, dockAlign, mapExpand, maximizeMinimize]);

  return <div className={`map-container`} id="device-map-container" ref={mapContainerRef}></div>;
};

export default EventsMap;
